// src/components/TipoEnvase/TipoEnvaseIndex.tsx

import React, { useState, useEffect, useRef } from "react";
import * as S from "./TipoEnvase.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, {
  SimpleItem,
  Label,
  RequiredRule,
  NumericRule,
} from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { ButtonGroup } from "devextreme-react/button-group";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { apiService } from "../../services/apiService";
import { ApiError } from "../Sucursales/SucursalesForm";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

// --- Tipos y Datos ---
interface TipoEnvase {
  id: string;
  codigo: string;
  descripcion: string;
  um: string;
  capacidad: number;
  estatusActivo: boolean;
}

// Opciones para el componente ButtonGroup
const estatusOptions = [
  { text: "Inactivo", value: false },
  { text: "Activo", value: true },
];

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const TipoEnvaseIndex = () => {
  const [dataSource, setDataSource] = useState<TipoEnvase[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<TipoEnvase> | null>(
    null
  );
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TipoEnvase | null>(null);
  const navigate = useNavigate(); // ✅ Hook para regresar

  const formRef = useRef<FormInstance | null>(null);

  const fetchTiposEnvase = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<TipoEnvase[]>(
        "/api/Catalogos/TiposEnvases"
      );
      setDataSource(response);
      return { data: response, error: null };
    } catch (err: any) {
      return { data: null, error: handleApiError(err) };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposEnvase();
  }, []);

  const handleAdd = () => {
    setEditingItem({ estatusActivo: true }); // Valor por defecto para nuevos items
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: TipoEnvase) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: TipoEnvase) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.patch(
          `/api/Catalogos/TiposEnvases/${itemToDelete.id}/cambiastatus`,
          ""
        );
        fetchTiposEnvase();
        notify("Cambio de estatus realizado con éxito.", "success", 2000);
      } catch (error) {
        console.error("Error al cambiar estatus:", error);
        notify("Error al cambiar el estatus del registro.", "error", 3000);
      } finally {
        setIsDeletePopupVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000
      );
      return;
    }

    const formData = form.option("formData") as TipoEnvase;

    try {
      if (formData.id) {
        const payload = {
          ...formData,
          usuarioDelUltimoCambio: "Sistema",
          fechaUltimoCambio: new Date().toISOString(),
        };
        const response = await apiService.put<TipoEnvase, TipoEnvase>(
          `/api/Catalogos/TiposEnvases/${formData.id}`,
          payload
        );
        setDataSource((prev) =>
          prev.map((item) => (item.id === formData.id ? response : item))
        );
        notify("Registro actualizado con éxito.", "success", 2000);
      } else {
        const payload = {
          ...formData,
          codigo: formData.codigo,
          descripcion: formData.descripcion,
          um: formData.um,
          capacidad: formData.capacidad,
        };
        const response = await apiService.post<TipoEnvase, TipoEnvase>(
          "/api/Catalogos/TiposEnvases",
          payload
        );
        setDataSource((prev) => [...prev, response]);
        notify("Registro creado con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
    } catch (error) {
      console.error("Error al guardar:", error);
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as TipoEnvase;
    const isActive = item.estatusActivo;

    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(item)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo tipos de envases</S.Header>
      <DataGrid
        dataSource={dataSource}
        keyExpr="id"
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <Paging defaultPageSize={10} />
        <HeaderFilter visible={true} />
        <FilterRow visible={true} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir tipo envase",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        <Column dataField="codigo" caption="Código" width={150}/>
        <Column dataField="descripcion" caption="Descripción" width={150}/>
        <Column dataField="um" caption="UM" width={150}/>
        <Column
          dataField="capacidad"
          caption="Capacidad"
          alignment="center"
          visible={false}
          width={150}
        />
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={150}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={150}
          cellRender={ActionsCell}
          
        />
      </DataGrid>

      {/* Popup de Edición / Creación */}
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem && editingItem.id
            ? "Editar Tipo de Envase"
            : "Añadir Tipo de Envase"
        }
        width={500}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Guardar",
              type: "success",
              stylingMode: "contained",
              onClick: handleSave,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              type: "default",
              stylingMode: "text",
              onClick: () => setIsFormPopupVisible(false),
            },
          },
        ]}
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="codigo">
              <Label text="Código" />
              <RequiredRule message="El codigo de envase es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="descripcion"
              editorType="dxTextArea"
              editorOptions={{ height: 100 }}
            >
              <Label text="Descripción" />
              <RequiredRule message="La descripción es requerida." />
            </SimpleItem>
            <SimpleItem dataField="um">
              <Label text="UM" />
              <RequiredRule message="La UM es requerida." />
            </SimpleItem>
            {/*<SimpleItem dataField="cantidadMaximo">
              <Label text="Capacidad maxima" />
              <RequiredRule message="La cantidad maxima es requerida." />
            </SimpleItem>*/}
            <SimpleItem dataField="capacidad">
              <Label text="Capacidad" />
              <RequiredRule message="La Capacidad es requerida." />
              <NumericRule />
            </SimpleItem>

            <SimpleItem
              dataField="estatusActivo"
              visible={false}
              render={() => (
                <ButtonGroup
                  items={estatusOptions}
                  keyExpr="value"
                  stylingMode="contained"
                  selectedItemKeys={
                    editingItem?.estatusActivo !== undefined
                      ? [editingItem.estatusActivo]
                      : []
                  }
                  onSelectionChanged={(e) => {
                    if (e.addedItems.length > 0) {
                      setEditingItem((prev) => ({
                        ...prev,
                        estatusActivo: e.addedItems[0].value,
                      }));
                      formRef.current?.updateData(
                        "estatusActivo",
                        e.addedItems[0].value
                      );
                    }
                  }}
                />
              )}
            >
              <Label text="Estatus" />
            </SimpleItem>
          </Form>
        </S.FormContainer>
      </Popup>

      {/* Popup de confirmación con texto dinámico */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle={true}
        title={`Confirmar ${
          itemToDelete?.estatusActivo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${
                itemToDelete?.estatusActivo ? "Desactivar" : "Activar"
              }`,
              type: itemToDelete?.estatusActivo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmDelete,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToDelete?.estatusActivo ? "desactivar" : "activar"}
            </strong>{" "}
            el tipo de envase
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.descripcion}"?
          </p>
        </div>
      </Popup>
    </S.PageWrapper>
  );
};

export default TipoEnvaseIndex;
